import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Card, Spinner, shortDate, useToast } from '../components/ui';
import type { HouseholdDevice, Language } from '../types';

const T = {
  en: {
    title: 'Kitchen tablet',
    intro:
      'A tablet in the kitchen should not ask anyone to sign in. Set one up here and it stays signed in for good — it can see the week and cook, but not change settings.',
    namePlaceholder: 'Kitchen iPad',
    create: 'Set up a device',
    linkTitle: 'Open this link on the tablet',
    linkHelp:
      'This is shown once. Open it in the tablet’s browser — Safari or Chrome, not a private window — and that browser is signed in from then on. Then add it to the home screen.',
    copy: 'Copy link',
    copied: 'Link copied',
    copyFailed: 'Could not copy — select the link and copy it by hand',
    done: 'Done',
    none: 'No devices set up yet.',
    added: 'Device created — open the link on the tablet',
    lastSeen: 'last used',
    never: 'not opened yet',
    revoke: 'Revoke',
    revoked: 'Device revoked',
    confirmRevoke: (name: string) => `Revoke ${name}? That browser will be signed out.`,
    since: 'added',
  },
  fr: {
    title: 'Tablette de cuisine',
    intro:
      'Une tablette dans la cuisine ne devrait demander à personne de se connecter. Configurez-en une ici : elle reste connectée définitivement, peut consulter la semaine et cuisiner, mais pas modifier les réglages.',
    namePlaceholder: 'iPad de la cuisine',
    create: 'Configurer un appareil',
    linkTitle: 'Ouvrez ce lien sur la tablette',
    linkHelp:
      'Affiché une seule fois. Ouvrez-le dans le navigateur de la tablette — pas en navigation privée — et ce navigateur restera connecté. Ajoutez ensuite l’app à l’écran d’accueil.',
    copy: 'Copier le lien',
    copied: 'Lien copié',
    copyFailed: 'Copie impossible — sélectionnez le lien à la main',
    done: 'Terminé',
    none: 'Aucun appareil configuré.',
    added: 'Appareil créé — ouvrez le lien sur la tablette',
    lastSeen: 'dernière utilisation',
    never: 'jamais ouvert',
    revoke: 'Révoquer',
    revoked: 'Appareil révoqué',
    confirmRevoke: (name: string) => `Révoquer ${name} ? Ce navigateur sera déconnecté.`,
    since: 'ajouté le',
  },
} as const;

/**
 * The kitchen tablet (§7).
 *
 * The claim link is the whole feature, and it is only ever shown once — the
 * token behind it signs a browser in permanently, so it is treated like a
 * password rather than left lying in a list.
 */
export function KitchenDeviceCard({ language }: { language: Language }) {
  const t = T[language];
  const toast = useToast();
  const [devices, setDevices] = useState<HouseholdDevice[] | null>(null);
  const [claimUrl, setClaimUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setDevices(await api.devices());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!devices) {
    return (
      <Card title={t.title}>
        <Spinner />
      </Card>
    );
  }

  const active = devices.filter((device) => !device.revoked_at);

  return (
    <Card title={t.title}>
      <p className="dim" style={{ marginBottom: 14 }}>
        {t.intro}
      </p>

      {claimUrl && (
        <div className="claim-link">
          <strong>{t.linkTitle}</strong>
          <code>{claimUrl}</code>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn primary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(claimUrl);
                  toast(t.copied);
                } catch {
                  // Clipboard access is refused outside a secure context, and
                  // the link is right there to select, so say so rather than
                  // failing silently.
                  toast(t.copyFailed);
                }
              }}
            >
              {t.copy}
            </button>
            <button className="btn" onClick={() => setClaimUrl(null)}>
              {t.done}
            </button>
          </div>
          <p className="dim" style={{ fontSize: 13, margin: 0 }}>
            {t.linkHelp}
          </p>
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        {active.length === 0 && !claimUrl && <p className="dim">{t.none}</p>}
        {active.map((device) => (
          <div className="access-row" key={device.id}>
            <span className="access-email">{device.name}</span>
            <span className="dim">
              {device.last_seen_at
                ? `${t.lastSeen} ${shortDate(device.last_seen_at.slice(0, 10), language)}`
                : t.never}
            </span>
            <button
              className="btn small danger"
              disabled={busy}
              onClick={async () => {
                if (!confirm(t.confirmRevoke(device.name))) return;
                setBusy(true);
                try {
                  await api.revokeDevice(device.id);
                  await load();
                  toast(t.revoked);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t.revoke}
            </button>
          </div>
        ))}
      </div>

      <form
        className="row"
        style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}
        onSubmit={async (event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const name = String(new FormData(form).get('name') ?? '').trim();
          if (!name) return;
          setBusy(true);
          try {
            const device = await api.createDevice(name);
            setClaimUrl(device.claimUrl);
            form.reset();
            await load();
            toast(t.added);
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not create that device');
          } finally {
            setBusy(false);
          }
        }}
      >
        <input
          name="name"
          type="text"
          required
          placeholder={t.namePlaceholder}
          style={{ flex: 2, minWidth: 180 }}
          disabled={busy}
        />
        <button className="btn" type="submit" disabled={busy}>
          {t.create}
        </button>
      </form>
    </Card>
  );
}
